/**
 * Telemetry Module
 *
 * Handles Sentry integration and screen specifications reporting.
 */

import * as Sentry from "@sentry/browser";
import axios from "axios";
import { ApiEndpoints } from "./config.js";
import { detectOS } from "./utils.js";

/**
 * Sets the Sentry user context.
 *
 * @param kwInput - The KW number input element.
 * @param userNameInput - The username input element.
 * @returns void
 */
export function setSentryUser(kwInput: HTMLInputElement, userNameInput: HTMLInputElement): void {
  Sentry.setUser({
    id: kwInput.value,
    username: userNameInput.value,
  });
}

/**
 * Sets the Sentry location context.
 *
 * @param option - The option element that was selected.
 * @returns void
 */
export function setSentryLocation(option: HTMLOptionElement | undefined): void {
  if (!option) {
    console.warn("No option provided to setSentryLocation");
    return;
  }

  Sentry.setContext("Location", {
    id: option.id,
    name: option.label,
  });
}

/**
 * Posts the screen specifications to the server.
 *
 * @param kwInput - The KW number input element.
 * @returns A promise that resolves when the screen specifications are successfully posted.
 */
export async function postScreenSpecs(kwInput: HTMLInputElement): Promise<void> {
  const kwNumber = kwInput.value.slice(-5);
  const screenSpecs = {
    ScreenHeightPixels: window.screen.height,
    ScreenWidthPixels: window.screen.width,
    ExecutionPlatform: "cloud",
    ProductName: "SE",
    KWNumber: kwNumber,
    ClientOS: detectOS(navigator),
  };

  try {
    await axios.post(ApiEndpoints.SCREEN_SPECS, screenSpecs);
  } catch (error) {
    Sentry.captureException(error);
    console.warn(`ERROR: Error posting screen specs: ${error}`);
  }
}
